import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import { flattenNestedArray } from '../helpers/flattenNestedArray';
import { breakpoints } from '../helpers/breakpoints';
import { motion } from 'framer-motion';
import Modal from 'react-modal';
import { StyledFestivalDates } from './festival-page';
import Layout from '../components/layout';
import SEO from '../components/seo';
import Main from '../components/main';
import BackLink from '../components/back-link';
import PropertyCard from '../components/property-card';
import InfoBlock from '../components/info-block';
import HowToBookContent from '../components/how-to-book-content';

import MapIconWhite from '../../static/map-icon--white.svg';
import HelpIcon from '../../static/help-hand-icon.svg';

const StyledPropertiesHeader = styled.header`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: 3rem;
  margin-left: auto;
  margin-right: auto;
  padding-right: var(--gutter);
  max-width: ${breakpoints.mw};
  @media screen and (min-width: ${breakpoints.lg}) {
    margin-bottom: 6rem;
  }
`;

const StyledPropertiesHeaderImagery = styled.div`
  width: 100%;
  height: 25rem;
  overflow: hidden;
  border-radius: 0 var(--border-radius) var(--border-radius) 0;
  @media screen and (min-width: ${breakpoints.md}) {
    height: 35rem;
  }
`;

const StyledPropertiesLayout = styled.div`
  display: grid;
  grid-gap: var(--gutter);
  margin: var(--v-spacing) auto;
  /* outline: solid 0.5px red; */
  max-width: ${breakpoints.xl};
  @media screen and (min-width: ${breakpoints.xl}) {
    grid-template-columns: auto 32rem;
  }
`;

const StyledHelpCol = styled.aside`
  display: none;
  @media screen and (min-width: ${breakpoints.xl}) {
    display: block;
    grid-column: 2;
    grid-row: 1 / -1;
  }
`;

const PropertiesGrid = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: var(--gutter);
  grid-column: 1 / 2;
  grid-row: 1;
  @media screen and (min-width: ${breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: ${breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media screen and (min-width: ${breakpoints.xl}) {
    grid-template-columns: repeat(2, 32rem);
  }
`;

const spring = {
  type: 'spring',
  damping: 20,
  stiffness: 300,
};

const StyledFilterWrapper = styled.div`
  position: sticky;
  top: 0;
  padding: 2rem 0;
  background: var(--c-white);
  z-index: 5;
  margin-left: -2rem;
  margin-right: -2rem;
  padding-left: 2rem;
  padding-right: 2rem;
  box-shadow: var(--shadow);
  display: flex;
  align-items: center;
  @media screen and (min-width: ${breakpoints.lg}) {
    box-shadow: none;
  }
  label {
    font-size: 1.6rem;
    margin-right: 1rem;
    font-weight: 700;
  }
`;

const StyledSelectWrapper = styled.div`
  border: solid 1px var(--c-black);
  flex-grow: 1;
  border-radius: var(--border-radius);
  @media screen and (min-width: ${breakpoints.md}) {
    max-width: 12rem;
  }
  select {
    all: unset;
    appearance: none;
    padding: 1rem;
    font-size: 1.4rem;
    width: 100%;
    font-weight: 300;
  }
`;

const modalStyles = {
  overlay: {
    zIndex: 1000,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    border: 'none',
    boxShadow: 'var(--shadow)',
    maxWidth: '992px',
    borderRadius: 'var(--border-radius)',
  },
};

const PropertiesPage = ({ data }) => {
  const [sleepAmount, setSleepAmount] = useState('all');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { contentfulFestival: festival } = data;
  const { properties } = festival;
  const allProperties = properties.map(property => {
    return {
      name: property.name,
      url: property.url,
      homes: property.homes,
      bookingIncentive: property.bookingIncentive,
    };
  });
  const sleepsRaw = flattenNestedArray(allProperties.map(property => property.homes.map(home => home.sleeps)));
  const sleeps = Array.from(new Set(sleepsRaw));
  const ogSocialImage = festival.image.fixed.src.slice(2);
  return (
    <Layout>
      <SEO title={`Homes near ${festival.name}`} ogImage={ogSocialImage} />
      <StyledPropertiesHeader>
        <StyledPropertiesHeaderImagery>
          <Img
            fluid={festival.image.fluid}
            alt={`Image of ${festival.name}`}
            style={{
              height: '100%',
            }}
          />
        </StyledPropertiesHeaderImagery>
      </StyledPropertiesHeader>
      <Main>
        <BackLink to={`/festivals/${festival.url}`} text={`Back to ${festival.name}`} />
        <h1>
          Homes near <span style={{ color: 'var(--c-orange)' }}>{festival.name}</span>
        </h1>
        <StyledFestivalDates>
          {festival.city}, {festival.country}
        </StyledFestivalDates>
        <StyledFilterWrapper>
          <label htmlFor="room">Sleeps:</label>
          <StyledSelectWrapper>
            <select id="room" onChange={e => setSleepAmount(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}>
              <option value="all">All</option>
              {sleeps.map(item => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </StyledSelectWrapper>
        </StyledFilterWrapper>
        <StyledPropertiesLayout>
          {properties ? (
            <PropertiesGrid>
              {allProperties.map(property => {
                const { url } = property;
                const homes = property.homes;
                return homes
                  .filter(home => {
                    if (sleepAmount === 'all') {
                      return true;
                    }
                    return home.sleeps === sleepAmount;
                  })
                  .map((home, index) => (
                    <motion.div positionTransition={spring} key={home.name}>
                      <PropertyCard
                        title={home.name}
                        imageSrc={home.images[0].fluid}
                        linkTo={`/festivals/${festival.url}/properties/${url}`}
                        price={home.price}
                        sleeps={[home.sleeps]}
                      />
                    </motion.div>
                  ));
              })}
            </PropertiesGrid>
          ) : (
            <p>Sorry, there are no properties for this festival :(</p>
          )}
          <StyledHelpCol>
            <InfoBlock onClick={() => setModalIsOpen(true)}>
              <img src={MapIconWhite} />
              <h3>How to book</h3>
              <p>Having trouble? See how easy it is to book</p>
            </InfoBlock>
            <InfoBlock bgColour={'var(--c-orange)'}>
              <img src={HelpIcon} />
              <h3>Help desk</h3>
              <p>Not finding what you're looking for? Get in touch!</p>
            </InfoBlock>
            <Modal isOpen={modalIsOpen} style={modalStyles} onRequestClose={() => setModalIsOpen(false)} contentLabel="Example Modal">
              <div className="modal-header">
                <h1>How to book</h1>
              </div>
              <HowToBookContent />
            </Modal>
          </StyledHelpCol>
        </StyledPropertiesLayout>
      </Main>
    </Layout>
  );
};

export default PropertiesPage;

export const PropertiesPageQuery = graphql`
  query($url: String!) {
    contentfulFestival(url: { eq: $url }) {
      name
      city
      country
      url
      image {
        fluid(maxWidth: 1680, quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
        fixed(width: 1200, height: 630) {
          src
        }
      }
      properties {
        id
        name
        url
        bookingIncentive
        homes {
          name
          price
          sleeps
          images {
            fluid(maxWidth: 320, maxHeight: 160, quality: 100) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
        }
      }
    }
  }
`;
